import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  useColorStyle,
  useColorMode,
  Stack,
  Tabs,
  TabList,
  Text,
  Skeleton,
} from '@tonic-one/react';
import { useQueries } from '@tanstack/react-query';
import CustomTab from '../../pages/Quarantine/RiskAI/DetectionsTabs/CustomTab';
import { MailRendererProps, ViewMode } from './types';
import { useTranslation } from 'react-i18next';
import {
  sanitizeEmailContent,
  createIframeDocument,
  fetchImageThroughProxy,
  imageProxyQueryKeys,
} from './sanitization';

/**
 * Secure email renderer component
 * Uses DOMPurify to sanitize HTML content and prevent XSS attacks.
 */
export const MailRenderer: React.FC<MailRendererProps> = ({
  htmlContent,
  textContent,
  showViewToggle = true,
  imageProxy,
  ...flexProps
}) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [viewMode, setViewMode] = useState<ViewMode>('html');
  const [tabIndex, setTabIndex] = useState(0); // 0 for html, 1 for text
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    setViewMode(index === 0 ? 'html' : 'text');
  };

  const sanitizedResult = useMemo(() => {
    return sanitizeEmailContent(htmlContent, imageProxy);
  }, [htmlContent, imageProxy]);

  // Use React Query to fetch all images with infinite caching
  const imageQueries = useQueries({
    queries:
      sanitizedResult && imageProxy?.enabled
        ? sanitizedResult.imagesToFetch.map(({ originalSrc }) => ({
            queryKey: imageProxyQueryKeys.image(
              imageProxy.baseUrl,
              originalSrc
            ),
            queryFn: () =>
              fetchImageThroughProxy(originalSrc, imageProxy.baseUrl),
            staleTime: Infinity,
            gcTime: Infinity,
          }))
        : [],
  });

  // Determine what content to show
  const hasHtml = Boolean(sanitizedResult?.sanitizedHtml);
  const hasText = Boolean(textContent);
  const isHtmlView = viewMode === 'html' && hasHtml;

  // Check if all images are loaded
  const allImagesLoaded = imageQueries.every((query) => !query.isLoading);
  const isLoadingImages = imageQueries.some((query) => query.isLoading);

  // Process HTML with loaded images
  const processedHtml = useMemo(() => {
    if (!sanitizedResult || !imageProxy?.enabled || !allImagesLoaded) {
      return sanitizedResult?.sanitizedHtml || '';
    }

    const { sanitizedHtml, imagesToFetch } = sanitizedResult;
    let currentHtml = sanitizedHtml;

    // Replace placeholders with blob URLs or remove failed images
    imagesToFetch.forEach(({ placeholder }, index) => {
      const query = imageQueries[index];
      if (query?.data) {
        // Replace with blob URL and remove placeholder data attribute
        currentHtml = currentHtml.replace(
          new RegExp(
            `<img([^>]*)data-proxy-placeholder="${placeholder}"([^>]*)>`,
            'g'
          ),
          `<img$1src="${query.data}"$2>`
        );
      } else if (!query?.isLoading) {
        // Remove failed images
        currentHtml = currentHtml.replace(
          new RegExp(
            `<img[^>]*data-proxy-placeholder="${placeholder}"[^>]*>`,
            'g'
          ),
          ''
        );
      }
    });

    return currentHtml;
  }, [sanitizedResult, imageProxy, imageQueries, allImagesLoaded]);

  // Create iframe content only when images are loaded
  const iframeContent = useMemo(() => {
    if (!processedHtml || isLoadingImages) return '';
    return createIframeDocument(processedHtml, imageProxy);
  }, [processedHtml, imageProxy, isLoadingImages]);

  // Update iframe content when it changes
  useEffect(() => {
    if (iframeRef.current && iframeContent && isHtmlView) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;

      if (doc) {
        doc.open();
        doc.write(iframeContent);
        doc.close();
      }
    }
  }, [iframeContent, isHtmlView]);

  // If no content is available
  if (!hasHtml && !hasText) {
    return (
      <Box p="4x" textAlign="center" color={colorStyle.color.secondary}>
        {t('common.noData')}
      </Box>
    );
  }

  return (
    <Stack flex={1}>
      {/* View toggle buttons */}
      {showViewToggle && hasHtml && hasText && (
        <Tabs index={tabIndex} mb="4x">
          <TabList>
            <CustomTab index={0} onClick={() => handleTabChange(0)}>
              <Text>{t('operations.euq.mailViewer.html')}</Text>
            </CustomTab>
            <CustomTab index={1} onClick={() => handleTabChange(1)}>
              <Text>{t('operations.euq.mailViewer.text')}</Text>
            </CustomTab>
          </TabList>
        </Tabs>
      )}

      {/* Email content container */}
      <Flex
        overflow="auto"
        border="1px solid"
        borderColor={colorStyle.background.fourth}
        p="3x"
        alignItems="center"
        backgroundColor={colorStyle.background.primary}
        height={isHtmlView ? 450 : 250}
        {...flexProps}
      >
        {isHtmlView ? (
          isLoadingImages ? (
            <Skeleton height="100%" width="100%" />
          ) : (
            <iframe
              ref={iframeRef}
              title="Email content"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'transparent',
              }}
              sandbox="allow-same-origin"
              srcDoc={iframeContent}
              referrerPolicy="no-referrer"
              loading="lazy"
            />
          )
        ) : (
          <Box
            as="pre"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            m={0}
            fontFamily="inherit"
            fontSize="inherit"
            lineHeight="1.5"
            height="100%"
            width="100%"
          >
            {textContent}
          </Box>
        )}
      </Flex>
    </Stack>
  );
};

export default MailRenderer;
